import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";

const Pip = ({ cx, cy }) => <circle cx={cx} cy={cy} r="5" fill="#0E131B" />;

const DiceArt = () => (
  <svg viewBox="0 0 160 110" className="h-full w-full" aria-hidden="true">
    <g transform="rotate(-12 55 55)"><rect x="20" y="20" width="70" height="70" rx="14" fill="#F5A30A" />
      <Pip cx="38" cy="38" /><Pip cx="55" cy="55" /><Pip cx="72" cy="72" /></g>
    <g transform="rotate(10 110 60)"><rect x="80" y="28" width="62" height="62" rx="12" fill="#F4F6F8" />
      <Pip cx="96" cy="44" /><Pip cx="126" cy="44" /><Pip cx="96" cy="74" /><Pip cx="126" cy="74" /></g>
  </svg>
);

const LudoArt = () => {
  const c = [["#E5484D", 0, 0], ["#30A46C", 1, 0], ["#3E8BFF", 0, 1], ["#F5C518", 1, 1]];
  return (
    <svg viewBox="0 0 110 110" className="h-full w-full" aria-hidden="true">
      <rect x="5" y="5" width="100" height="100" rx="10" fill="#F4F6F8" />
      {c.map(([col, i, j]) => (
        <g key={col}><rect x={8 + i * 58} y={8 + j * 58} width="36" height="36" rx="6" fill={col} />
          <circle cx={26 + i * 58} cy={26 + j * 58} r="7" fill="#F4F6F8" /></g>
      ))}
      <polygon points="55,40 70,55 55,70 40,55" fill="#0E131B" />
    </svg>
  );
};

const DamesArt = () => (
  <svg viewBox="0 0 110 110" className="h-full w-full" aria-hidden="true">
    <rect x="3" y="3" width="104" height="104" rx="10" fill="#5A3A22" />
    {Array.from({ length: 25 }, (_, k) => {
      const r = Math.floor(k / 5), q = k % 5;
      return <rect key={k} x={9 + q * 18.4} y={9 + r * 18.4} width="18.4" height="18.4" fill={(r + q) % 2 ? "#7B4F2C" : "#E8D3AE"} />;
    })}
    {[[1, 0], [3, 0], [0, 1], [2, 1], [4, 1]].map(([q, r]) => <circle key={`b${q}${r}`} cx={18 + q * 18.4} cy={18 + r * 18.4} r="7" fill="#17120E" stroke="#3a2c22" strokeWidth="1.5" />)}
    {[[1, 4], [3, 4], [0, 3], [2, 3], [4, 3]].map(([q, r]) => <circle key={`w${q}${r}`} cx={18 + q * 18.4} cy={18 + r * 18.4} r="7" fill="#F3E9D2" stroke="#cdbb95" strokeWidth="1.5" />)}
  </svg>
);

const GAMES = [
  { k: "dice", Art: DiceArt, to: "/lobby" },
  { k: "ludo", Art: LudoArt, to: "/lobby?game=ludo" },
  { k: "dames", Art: DamesArt, to: "/dames/essai", open: true },
];

export const GamesShowcase = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <section className="py-10" data-testid="landing-games">
      <h2 className="mb-6 font-display text-xl font-bold tracking-tight sm:text-2xl">{t("landing.gamesTitle")}</h2>
      <div className="grid gap-4 md:grid-cols-3">
        {GAMES.map(({ k, Art, to, open }, i) => (
          <div key={k} className="surface rise flex flex-col overflow-hidden" style={{ animationDelay: `${i * 90}ms` }} data-testid={`landing-game-${k}`}>
            <div className="grid h-40 place-items-center bg-gradient-to-b from-white/[0.06] to-transparent p-5">
              <div className="h-28 w-full max-w-[160px] transition-transform duration-300 hover:-rotate-2 hover:scale-105"><Art /></div>
            </div>
            <div className="flex flex-1 flex-col gap-3 p-5">
              <p className="font-display text-lg font-bold">{t(`landing.${k}Name`)}</p>
              <p className="flex-1 text-sm text-slate-400">{t(`landing.${k}Line`)}</p>
              {to ? (
                <Link to={user || open ? to : `/auth?mode=signup`} data-testid={`landing-game-${k}-play`}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-amber-500 font-bold text-[#0B0D12] transition-colors hover:bg-amber-400">
                  {open ? t("landing.tryFree") : t("landing.play")} <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <span className="inline-flex h-11 items-center justify-center rounded-full border border-white/10 text-sm font-semibold text-slate-400" data-testid={`landing-game-${k}-soon`}>{t("landing.soon")}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
